import { useNavigate, useParams } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { Button, TextInput, Checkbox } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconArrowRight } from "@tabler/icons-react";
import { checkActivationToken, activateUser } from "../../../api";
import { BarRegistrationInput } from "./FormComponents";
import { nonProtectedRoutes } from "../../../utils/nonProtectedRoutingPaths";

export const ActivationForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState({
    success: false,
    message: "",
  });
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const { token } = useParams();

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await checkActivationToken(token);
      if (!response.data || response.status !== 200) {
        navigate(nonProtectedRoutes.NOT_FOUND, { replace: true });
      }
    } catch (error) {
      console.error("Token validation error:", error);
      navigate(nonProtectedRoutes.NOT_FOUND, { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const form = useForm({
    initialValues: {
      password: "",
      first_name: "",
      last_name: "",
      bar_jurisdiction: "",
      bar_registration_number: "",
      terms_accepted: false,
      attorney_confirmation: false,
      agree_to_marketing: false,
    },
    validate: {
      password: (value) =>
        value.length < 8 ? "Password must be at least 8 characters" : null,
      first_name: (value) => (value.trim() ? null : "First name is required"),
      last_name: (value) => (value.trim() ? null : "Last name is required"),
      terms_accepted: (value) =>
        value ? null : "You must agree to the Terms of Service",
      attorney_confirmation: (value) =>
        value
          ? null
          : "You must confirm your status as a licensed attorney or working under direct supervision",
    },
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitResult({ success: false, message: "" });

    try {
      const activationData = {
        ...values,
        token: token,
      };

      const response = await activateUser(token, activationData);
      console.log("response :", response);

      if (response.data && response.data.access_token) {
        localStorage.setItem("access_token", response.data.access_token);
        navigate("/app/dashboard");
      }
    } catch (error) {
      console.error("Activation error:", error);

      if (error.response && error.response.data) {
        const fieldErrors = error.response.data;

        const formattedErrors = {};
        Object.keys(fieldErrors).forEach((key) => {
          formattedErrors[key] = Array.isArray(fieldErrors[key])
            ? fieldErrors[key][0]
            : fieldErrors[key];
        });

        form.setErrors(formattedErrors);

        setSubmitResult({
          success: false,
          message: "Please correct the errors in the form.",
        });
      } else if (error.request) {
        setSubmitResult({
          success: false,
          message:
            "No response from server. Please check your connection and try again.",
        });
      } else {
        setSubmitResult({
          success: false,
          message: "An error occurred. Please try again later.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6 my-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Activate your account
      </h2>

      {submitResult.message && (
        <div
          className={`p-4 mb-6 rounded-md ${submitResult.success ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}
        >
          {submitResult.message}
        </div>
      )}

      <form onSubmit={form.onSubmit(handleSubmit)} className="space-y-4">
        <TextInput
          label="Password"
          type="password"
          placeholder="Enter your password"
          required
          {...form.getInputProps("password")}
          className="w-full"
          error={form.errors.password}
        />

        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label="First Name"
            placeholder="Enter first name"
            required
            {...form.getInputProps("first_name")}
            className="w-full"
            error={form.errors.first_name}
          />

          <TextInput
            label="Last Name"
            placeholder="Enter last name"
            required
            {...form.getInputProps("last_name")}
            className="w-full"
            error={form.errors.last_name}
          />
        </div>

        <TextInput
          label="Bar Jurisdiction"
          placeholder="Enter your bar jurisdiction"
          {...form.getInputProps("bar_jurisdiction")}
          className="w-full"
        />

        <BarRegistrationInput form={form} />

        <Checkbox
          label={
            <span>
              I agree to the &nbsp;
              <a
                href={nonProtectedRoutes.TERMS}
                className="text-blue-600 hover:underline"
                target="_blank"
              >
                Terms of Service
              </a>
            </span>
          }
          {...form.getInputProps("terms_accepted", { type: "checkbox" })}
          error={form.errors.terms_accepted}
          className="mt-4"
        />

        <Checkbox
          label="I agree to receive marketing emails from Socius Legal"
          {...form.getInputProps("attorney_confirmation", { type: "checkbox" })}
          error={form.errors.attorney_confirmation}
          className="mt-4"
        />
        <Checkbox
          label="I consent to receive emails from Socius Legal (we will never share your email address with anyone)"
          {...form.getInputProps("agree_to_marketing", { type: "checkbox" })}
          className="mt-4"
        />

        <div className="pt-4">
          <Button
            type="submit"
            disabled={isSubmitting}
            fz="1.1rem"
            size="md"
            fullWidth
            rightSection={<IconArrowRight size={18} />}
            style={{
              backgroundColor: "var(--accent-gold)",
              transition: "background-color 0.3s ease-in-out",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "var(--accent-gold-hover)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--accent-gold)")
            }
          >
            {isSubmitting ? "Activating..." : "Activate Account"}
          </Button>
        </div>
      </form>
    </div>
  );
};
